import { useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CATEGORIES } from "@/lib/data";
import { getDeals } from "@/lib/content";
import DealsTable from "@/components/DealsTable";
import FaqAccordion from "@/components/FaqAccordion";
import Cta from "@/components/Cta";

const FIELD_MAP = {
  concert: ["event", "location", "date", "price", "provider"],
  festival: ["event", "location", "date", "price", "provider"],
  flight: ["event", "location", "date", "price", "provider"],
  attraction: ["event", "location", "date", "price", "provider"],
};

export default function CategoryPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "");
  const cfg = CATEGORIES[slug];

  if (!cfg) return <Navigate to="/" replace />;

  const deals = getDeals(cfg.key);

  return (
    <div data-testid={`category-page-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={cfg.image} alt={cfg.eyebrow} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C3E50]/72" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center text-white">
          <span className="inline-block text-xs uppercase tracking-[0.2em] font-medium bg-white/15 backdrop-blur px-4 py-1.5 rounded-full mb-5">
            {cfg.eyebrow}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter max-w-3xl mx-auto"
          >
            {cfg.title}
          </motion.h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl mx-auto">{cfg.subtitle}</p>
          <a
            href="#deals"
            data-testid="category-hero-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2A] text-white font-semibold rounded-full px-8 py-4 transition-all hover:-translate-y-1 shadow-lg"
          >
            Bekijk Deals <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* DEALS */}
      <section id="deals" className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 scroll-mt-20">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-[#2C3E50] mb-10">
          Vergelijk &amp; Bespaar
        </h2>
        <DealsTable columns={cfg.tableCols} rows={deals} fields={FIELD_MAP[cfg.key]} />
      </section>

      {/* FAQ */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-[#2C3E50] mb-10">
          ❓ Veelgestelde Vragen
        </h2>
        <FaqAccordion items={cfg.faqs} />
      </section>

      <Cta
        title="Klaar om te Besparen?"
        text="Begin nu met het vinden van de beste deals in deze categorie!"
        buttons={[
          { to: `/${slug}`, label: "Bekijk alle deals" },
          { to: "/blog", label: "Lees onze tips" },
        ]}
      />
    </div>
  );
}
